/**
 * Tela /catalog/stage — TH-55150 (M-seed.7).
 *
 * Fluxo de revisão dos produtos coletados pelos scrapers (Cosmos, APAS,
 * GS1, fabricantes, Open Food Facts). Apenas super_admin acessa.
 *
 * UX:
 * - Lista paginada com thumbnail + nome + marca + fornecedor + EANs.
 * - Filtros: source (dropdown), brand_name (ILIKE), supplier_name (ILIKE).
 * - Por row: Aprovar | Rejeitar.
 * - Bulk: checkboxes + Aprovar todos / Rejeitar todos.
 * - Auto-invalidate da query após cada mutation.
 *
 * Endpoints consumidos via `useCatalogStage` (TanStack Query):
 *   GET    /api/catalog/stage-products
 *   POST   /api/catalog/stage-products/{id}/approve
 *   POST   /api/catalog/stage-products/{id}/reject
 *   POST   /api/catalog/stage-products/bulk-approve
 *   POST   /api/catalog/stage-products/bulk-reject
 */

import { useMemo, useState } from "react";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Pagination } from "@/components/ui/Pagination";
import { Skeleton } from "@/components/ui/Skeleton";
import { useAuth } from "@/hooks/useAuth";
import {
  useApproveStage,
  useBulkApproveStage,
  useBulkRejectStage,
  useCatalogStage,
  useRejectStage,
  type StageProduct,
  type StageSource,
} from "@/hooks/useCatalogStage";

const PER_PAGE = 25;

const SOURCE_LABELS: Record<StageSource, string> = {
  apas: "APAS",
  gs1: "GS1",
  fabricante: "Fabricante",
  cosmos: "Cosmos",
  off: "OpenFoodFacts",
  manual: "Manual",
};

const SOURCE_OPTIONS: (StageSource | "")[] = [
  "",
  "apas",
  "gs1",
  "fabricante",
  "cosmos",
  "off",
  "manual",
];

function relativeTime(iso: string): string {
  const d = new Date(iso).getTime();
  const diff = Date.now() - d;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export function CatalogStagePage() {
  const { user } = useAuth();
  const canAccess = user?.role === "super_admin";

  const [page, setPage] = useState(1);
  const [source, setSource] = useState<StageSource | "">("");
  const [brandFilter, setBrandFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const filters = useMemo(
    () => ({
      status: "pending" as const,
      source: source || undefined,
      brand_name: brandFilter || undefined,
      supplier_name: supplierFilter || undefined,
      page,
      per_page: PER_PAGE,
    }),
    [source, brandFilter, supplierFilter, page],
  );

  const query = useCatalogStage(filters);
  const approveMutation = useApproveStage();
  const rejectMutation = useRejectStage();
  const bulkApproveMutation = useBulkApproveStage();
  const bulkRejectMutation = useBulkRejectStage();

  const rows = query.data?.data ?? [];
  const total = query.data?.meta.total ?? 0;

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const visibleIds = rows.map((r) => r.id);
      const allSelected = visibleIds.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        for (const id of visibleIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of visibleIds) next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const handleBulkApprove = () => {
    if (selected.size === 0) return;
    bulkApproveMutation.mutate(Array.from(selected), {
      onSuccess: clearSelection,
    });
  };

  const handleBulkReject = () => {
    if (selected.size === 0) return;
    bulkRejectMutation.mutate(Array.from(selected), {
      onSuccess: clearSelection,
    });
  };

  if (user && !canAccess) {
    return (
      <RequireAuth>
        <AppLayout breadcrumb="CATÁLOGO / STAGE" title="Acesso negado">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Esta tela é exclusiva de super_admin.
          </p>
        </AppLayout>
      </RequireAuth>
    );
  }

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="CATÁLOGO / STAGE"
        title={`Revisão de stage (${total} pendentes)`}
      >
        <div className="space-y-4">
          {/* Filtros */}
          <Card className="p-4">
            <div className="grid gap-3 md:grid-cols-4">
              <div>
                <label
                  htmlFor="filter-source"
                  className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]"
                >
                  Fonte
                </label>
                <select
                  id="filter-source"
                  value={source}
                  onChange={(e) => {
                    setSource(e.target.value as StageSource | "");
                    setPage(1);
                  }}
                  className="h-10 w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-card)] px-3 text-sm"
                >
                  {SOURCE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s === "" ? "Todas" : SOURCE_LABELS[s]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="filter-brand"
                  className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]"
                >
                  Marca
                </label>
                <Input
                  id="filter-brand"
                  value={brandFilter}
                  placeholder="Buscar por marca…"
                  onChange={(e) => {
                    setBrandFilter(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="filter-supplier"
                  className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]"
                >
                  Fornecedor
                </label>
                <Input
                  id="filter-supplier"
                  value={supplierFilter}
                  placeholder="Buscar por fornecedor…"
                  onChange={(e) => {
                    setSupplierFilter(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div className="flex items-end">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSource("");
                    setBrandFilter("");
                    setSupplierFilter("");
                    setPage(1);
                  }}
                >
                  Limpar
                </Button>
              </div>
            </div>
          </Card>

          {/* Bulk bar */}
          {selected.size > 0 && (
            <Card className="flex items-center justify-between p-3">
              <span className="text-sm">
                <strong className="tabular-nums">{selected.size}</strong>{" "}
                selecionado{selected.size === 1 ? "" : "s"}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleBulkApprove}
                  disabled={bulkApproveMutation.isPending}
                >
                  Aprovar selecionados
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleBulkReject}
                  disabled={bulkRejectMutation.isPending}
                >
                  Rejeitar selecionados
                </Button>
                <Button size="sm" variant="ghost" onClick={clearSelection}>
                  Cancelar
                </Button>
              </div>
            </Card>
          )}

          {/* Lista */}
          {query.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-lg font-medium">Nenhum item pendente.</p>
              <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                Os scrapers podem estar vazios ou todos os itens já foram revisados.
              </p>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="border-b border-[var(--color-border)] bg-[var(--color-muted)]/30 text-xs uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
                  <tr>
                    <th className="w-10 px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label="Selecionar todos"
                        checked={
                          rows.length > 0 &&
                          rows.every((r) => selected.has(r.id))
                        }
                        onChange={toggleAllVisible}
                      />
                    </th>
                    <th className="w-16 px-3 py-2 text-left">Imagem</th>
                    <th className="px-3 py-2 text-left">Produto</th>
                    <th className="px-3 py-2 text-left">EANs</th>
                    <th className="px-3 py-2 text-left">Fonte</th>
                    <th className="px-3 py-2 text-left">Coletado</th>
                    <th className="px-3 py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <StageRow
                      key={row.id}
                      row={row}
                      checked={selected.has(row.id)}
                      onToggle={() => toggleSelected(row.id)}
                      onApprove={() => approveMutation.mutate(row.id)}
                      onReject={() => rejectMutation.mutate(row.id)}
                      busy={
                        approveMutation.isPending ||
                        rejectMutation.isPending
                      }
                    />
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {total > 0 && (
            <Pagination
              page={page}
              perPage={PER_PAGE}
              total={total}
              onPageChange={setPage}
            />
          )}
        </div>
      </AppLayout>
    </RequireAuth>
  );
}

interface StageRowProps {
  row: StageProduct;
  checked: boolean;
  onToggle: () => void;
  onApprove: () => void;
  onReject: () => void;
  busy: boolean;
}

function StageRow({
  row,
  checked,
  onToggle,
  onApprove,
  onReject,
  busy,
}: StageRowProps) {
  return (
    <tr className="border-b border-[var(--color-border)] last:border-0">
      <td className="px-3 py-3 align-top">
        <input
          type="checkbox"
          aria-label={`Selecionar ${row.product_name}`}
          checked={checked}
          onChange={onToggle}
        />
      </td>
      <td className="px-3 py-3 align-top">
        {row.image_url ? (
          <img
            src={row.image_url}
            alt={row.product_name}
            className="h-12 w-12 rounded border border-[var(--color-border)] object-contain"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded border border-dashed border-[var(--color-border)] text-xs text-[var(--color-muted-foreground)]">
            ?
          </div>
        )}
      </td>
      <td className="px-3 py-3 align-top">
        <div className="font-medium">{row.product_name}</div>
        <div className="text-xs text-[var(--color-muted-foreground)]">
          {row.brand_name ?? "—"}
          {row.supplier_name && row.supplier_name !== row.brand_name
            ? ` · ${row.supplier_name}`
            : ""}
          {row.category_hint ? ` · ${row.category_hint}` : ""}
        </div>
      </td>
      <td className="px-3 py-3 align-top">
        <div className="flex flex-wrap gap-1">
          {row.eans.length === 0 ? (
            <span className="text-xs text-[var(--color-muted-foreground)]">
              Sem EAN
            </span>
          ) : (
            row.eans.map((e) => (
              <Badge key={e.ean} variant="muted" className="font-mono text-xs">
                {e.ean}
              </Badge>
            ))
          )}
        </div>
      </td>
      <td className="px-3 py-3 align-top">
        <Badge variant="muted" className="text-xs uppercase tracking-wide">
          {SOURCE_LABELS[row.source as StageSource] ?? row.source}
        </Badge>
      </td>
      <td className="px-3 py-3 align-top text-xs text-[var(--color-muted-foreground)]">
        {relativeTime(row.created_at)}
      </td>
      <td className="px-3 py-3 text-right align-top">
        <div className="inline-flex gap-2">
          <Button size="sm" onClick={onApprove} disabled={busy}>
            Aprovar
          </Button>
          <Button size="sm" variant="ghost" onClick={onReject} disabled={busy}>
            Rejeitar
          </Button>
        </div>
      </td>
    </tr>
  );
}
